import { useEffect, useRef } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { LOANS_APP_ROLE_QUERY_KEY, isLoansAppRole, setStoredLoansAppRole } from '@/lib/app-role';
import { APP_SELECTOR_RETURN_TO, startKeycloakLogin } from '@/lib/keycloak';
import { getAppSelectorUrl } from '@/lib/runtime-urls';
import { useAuthStore } from '@/stores/auth-store';

const APP_SELECTOR_URL = getAppSelectorUrl();

function appendRoleToReturnTo(returnTo: string, appRole: string | null): string {
  if (!appRole || !isLoansAppRole(appRole)) {
    return returnTo;
  }

  const trimmedReturnTo = returnTo.trim();
  if (!trimmedReturnTo) {
    return returnTo;
  }

  try {
    const url = new URL(trimmedReturnTo, window.location.origin);
    if (!isLoansAppRole(url.searchParams.get(LOANS_APP_ROLE_QUERY_KEY))) {
      url.searchParams.set(LOANS_APP_ROLE_QUERY_KEY, appRole);
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return returnTo;
  }
}

export function Login() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const resetLocalSession = useAuthStore(s => s.resetLocalSession);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('reason') === 'session_expired';
  const authError = searchParams.get('error') || searchParams.get('message');
  const inviteRole = searchParams.get(LOANS_APP_ROLE_QUERY_KEY);
  const inviteReturnTo = searchParams.get('returnTo');
  const reloginRequested = searchParams.get('relogin') === '1';
  const hasClearedSessionRef = useRef(false);
  const requestedRoute =
    typeof location.state?.from === 'string' && location.state.from.trim().length > 0
      ? location.state.from
      : null;
  const defaultLoansReturnTo = isLoansAppRole(inviteRole) ? '/dashboard' : APP_SELECTOR_RETURN_TO;
  const baseReturnTo =
    requestedRoute ||
    (inviteReturnTo && inviteReturnTo.trim().length > 0 ? inviteReturnTo : null) ||
    defaultLoansReturnTo;
  const defaultReturnTo = appendRoleToReturnTo(baseReturnTo, inviteRole);
  const shouldAuthenticateForLoans = Boolean(
    (requestedRoute && requestedRoute !== '/') ||
    isLoansAppRole(inviteRole) ||
    (inviteReturnTo && inviteReturnTo !== '/'),
  );
  const needsSelectorSessionReset = reloginRequested && isAuthenticated;

  useEffect(() => {
    if (!needsSelectorSessionReset || hasClearedSessionRef.current) {
      return;
    }

    hasClearedSessionRef.current = true;
    resetLocalSession();
  }, [needsSelectorSessionReset, resetLocalSession]);

  useEffect(() => {
    if (needsSelectorSessionReset) {
      return undefined;
    }

    if (!shouldAuthenticateForLoans) {
      window.location.replace(APP_SELECTOR_URL);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (isLoansAppRole(inviteRole)) {
        setStoredLoansAppRole(inviteRole);
      }
      void startKeycloakLogin(defaultReturnTo);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [defaultReturnTo, inviteRole, needsSelectorSessionReset, shouldAuthenticateForLoans]);

  if (isAuthenticated && !reloginRequested) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="flex flex-col items-center gap-3 text-slate-500">
        <Spinner size="lg" className="h-7 w-7 animate-spin" />
        <p className="text-sm">
          {needsSelectorSessionReset
            ? 'Refreshing your Loans session...'
            : authError || sessionExpired
              ? 'Redirecting to the shared sign-in flow...'
              : 'Redirecting...'}
        </p>
      </div>
    </div>
  );
}
